import React from 'react';
import { Box, Switch, Typography } from '@mui/material';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import type { IntegrationSettings } from '../services/integrationApi';
import { SectionCard } from './ui';

export interface StoreSpecsPushSectionProps {
  settings: IntegrationSettings;
  onChange: (partial: Partial<IntegrationSettings>) => void;
  platform: string;
}

const T = 'integrations.storeSpecsPush';

/**
 * "Push my own spec values" (flag `store_override_specs_push`, base, MOT-413): the spec values a
 * store set on product details win over Motzu's catalog value on the Shopify metafields or
 * Lightspeed custom fields the store already mapped. Hidden with the flag off or on another platform.
 */
const StoreSpecsPushSection: React.FC<StoreSpecsPushSectionProps> = ({ settings, onChange, platform }) => {
  const { t } = useAppTranslation();
  const { enabled } = useOrgFeatureFlag('store_override_specs_push');
  if (!enabled || (platform !== 'shopify' && platform !== 'lightspeed')) return null;
  const label = t(`${T}.label`);
  const where = t(platform === 'shopify' ? `${T}.whereShopify` : `${T}.whereLightspeed`);

  return (
    <SectionCard title={t(`${T}.title`)} subtitle={t(`${T}.subtitle`)} dense padded>
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, mr: 2, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {label}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
            {t(`${T}.desc`, { where })}
          </Typography>
        </Box>
        <Switch
          size="small"
          checked={settings.pushStoreSpecs === true}
          onChange={(e) => onChange({ pushStoreSpecs: e.target.checked })}
          slotProps={{ input: { 'aria-label': label } }}
        />
      </Box>
    </SectionCard>
  );
};

export default StoreSpecsPushSection;
